import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Globe,
  Search,
  Wrench,
  Palette,
  Settings,
  Target,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "Website Development",
    description:
      "Custom-built, responsive websites designed for speed, performance, and conversion.",
    path: "/services/website-development",
    color: "from-violet-500 to-purple-600",
  },
  {
    icon: Search,
    title: "SEO Services",
    description:
      "Strategic search engine optimization to improve rankings and drive qualified organic traffic.",
    path: "/services/seo-services",
    color: "from-green-500 to-emerald-500",
  },
  {
    icon: Wrench,
    title: "Custom Tool Development",
    description:
      "Tailored digital tools and automation solutions to streamline business operations.",
    path: "/services/custom-tool-development",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Palette,
    title: "UI / UX Design",
    description:
      "User-focused design that enhances usability and improves engagement.",
    path: "/services/ui-ux-design",
    color: "from-orange-500 to-amber-500",
  },
  {
    icon: Settings,
    title: "Website Maintenance",
    description:
      "Ongoing technical support, security updates, and performance monitoring.",
    path: "/services/website-maintenance",
    color: "from-slate-500 to-gray-600",
  },
  {
    icon: Target,
    title: "Digital Strategy",
    description:
      "Data-driven planning to align your website and SEO efforts with business goals.",
    path: "/services/digital-strategy",
    color: "from-indigo-500 to-violet-500",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export const ServicesSection = () => {
  return (
    <section className="pt-8 sm:pt-10 lg:pt-16 pb-12 sm:pb-14 lg:pb-16 bg-muted/30 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-16 left-10 sm:top-20 sm:left-20 w-56 h-56 sm:w-72 sm:h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-16 right-10 sm:bottom-20 sm:right-20 w-72 h-72 sm:w-96 sm:h-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-14"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            OUR SERVICES
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-4 sm:mb-6">
            Complete Digital Solutions for{" "}
            <span className="gradient-text">Your Business</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            We provide website development and SEO services built to support long-term
            growth and measurable performance.
          </p>
        </motion.div>

        {/* Services Grid (mobile 1-col, md 2-col, lg 3-col) */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div key={index} variants={itemVariants} className="h-full">
                <Link to={service.path} className="block h-full">
                  <Card className="h-full p-5 sm:p-6 bg-card border-border hover:border-primary/30 transition-all duration-300 card-hover group flex flex-col">
                    {/* Icon */}
                    <div
                      className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-background" />
                    </div>

                    {/* Content */}
                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2 sm:mb-3 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                      {service.description}
                    </p>

                    {/* Link */}
                    <div className="flex items-center gap-2 text-primary font-medium mt-4 sm:mt-5 group-hover:gap-3 transition-all">
                      <span>Learn More</span>
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-10 sm:mt-14"
        >
          {/* <p className="text-muted-foreground mb-5 sm:mb-6">
            Not sure which service is right for you?
          </p> */}

          <Button
            variant="gradient"
            size="lg"
            asChild
            className="group w-full sm:w-auto justify-center"
          >
            {/* <Link to="/contact" className="flex items-center justify-center gap-2">
              Let's Discuss Your Project
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link> */}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};